import { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Dropdown, Menu } from 'antd';
import { Button } from 'uiKit/atoms/Button/Button';
import { PrimaryButton } from 'uiKit/atoms/Button/components/PrimaryButton';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { ReactComponent as HeartOutlinedSvg } from 'assets/img/heart-outlined.svg';
import { ReactComponent as MailSvg } from 'assets/img/mail.svg';
import { ModalsList } from 'constants/common';
import { useStores } from 'stores';
import BackHeader from 'components/BackHeader/BackHeader';
import styles from './ControlPanel.module.scss';
import { runInAction } from 'mobx';

const ControlPanel = () => {
  const { singleLotStore, lotStore, userStore, modalStore, messageStore } = useStores();
  const { title, userId, id, isFavorited, contact } = singleLotStore.singleLot;
  const { singleLotNotFound, active, isDraft } = singleLotStore;
  const { userProfile, isAdmin } = userStore;
  const { showModal } = modalStore;
  const newConversationId = -1;

  const [isFavorite, setIsFavorite] = useState(isFavorited);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  const [isConversationsLoaded, setIsConversationsLoaded] = useState(false);

  useEffect(() => {
    setIsFavorite(isFavorited);
  }, [isFavorited]);

  const navigate = useNavigate();
  const location = useLocation();

  const handleGoBack = () => {
    if (location.key) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const editLot = lotId => {
    lotStore.setCurrentLotId(lotId, navigate);
  };

  const changeActiveState = (lotId, isActive) =>
    isActive ? singleLotStore.deactivate(lotId, navigate) : singleLotStore.activate(lotId);

  const deleteLot = () => {
    lotStore.deleteLotAsync(id, navigate);
  };

  const handleDeleteButtonClick = () => {
    showModal(ModalsList.DeleteModal.name, deleteLot);
  };

  const addToFavoritesHandler = () => {
    setIsFavorite(true);
    lotStore.addToFavorite(id);
  };

  const removeFromFavoritesHandler = () => {
    setIsFavorite(false);
    lotStore.removeFromFavorite(id);
  };

  useEffect(() => {
    const loadConversations = async () => {
      await messageStore.getConversations();
      runInAction(() => {
        setIsConversationsLoaded(true);
      });
    };

    if (messageStore.conversations.length === 0) {
      loadConversations();
    } else {
      setIsConversationsLoaded(true);
    }
  }, [messageStore]);
  const openConversation = () => {
    const existingConversation = messageStore.conversations.find(conver => conver.lotId === id);

    if (existingConversation) {
      navigate(`/conversation/${existingConversation.id}`);
      messageStore.setConversation(existingConversation.id);
    } else {
      const newConversationData = {
        title,
        authorId: userId,
        userFirstName: contact.firstName,
        userLastName: contact.lastName,
        lastText: '',
        lotId: id,
        id: newConversationId,
      };
      sessionStorage.setItem('newConversationData', JSON.stringify(newConversationData));

      navigate(`/conversation/${newConversationId}`);
      messageStore.setConversation(newConversationId);
    }
  };

  const getFavourite = () => {
    if (userId !== userProfile.id) {
      return (
        <>
          {isFavorite ? (
            <button type="button" onClick={removeFromFavoritesHandler} className={styles.bns3_lot_page_favorite_button}>
              <HeartOutlinedSvg className={cn(styles.bns3_lot_favorite_icon, styles.active)} />
              Remove From Favorites
            </button>
          ) : (
            <button type="button" onClick={addToFavoritesHandler} className={styles.bns3_lot_page_favorite_button}>
              <HeartOutlinedSvg className={styles.bns3_lot_favorite_icon} />
              Add To Favorites
            </button>
          )}
        </>
      );
    }

    return null;
  };

  const getDeleteActionBtn = () => {
    if (userId === userProfile.id) {
      return (
        <Button variant="text" onClick={handleDeleteButtonClick} className={styles.bns3_lot_page_delete_button}>
          Delete
        </Button>
      );
    }

    return null;
  };

  const getActivationTriggerBtn = () => {
    if (!isDraft && userId === userProfile.id) {
      return (
        <Button
          variant="text"
          onClick={() => changeActiveState(id, active)}
          className={styles.bns3_add_page_cancel_button}
        >
          {active ? 'Deactivate' : 'Activate'}
        </Button>
      );
    }

    return null;
  };

  const getEditActionBtn = () => {
    if (userId === userProfile.id) {
      return (
        <div className={styles.bns3_lot_page_button_container}>
          <PrimaryButton onClick={() => editLot(id)} data-testid="contact-info-edit">
            EDIT
          </PrimaryButton>
        </div>
      );
    }

    return null;
  };

  const getSendMsgActionBtn = () => {
    if (userId !== userProfile.id || isAdmin) {
      return (
        <div className={styles.bns3_lot_page_button_container}>
          <PrimaryButton
            type="primary"
            shape="round"
            size="large"
            icon={<MailSvg className={styles.bns3_lot_page_message_icon} />}
            onClick={openConversation}
          >
            SEND MESSAGE
          </PrimaryButton>
        </div>
      );
    }

    return null;
  };

  const getControlBtns = () => {
    if (singleLotNotFound === null || !userId) return null;

    if (isAdmin && userId !== userProfile.id) {
      const menu = (
        <div className={styles.bns3_admin_actions_menu_container}>
          <Menu>
            <Menu.Item key="1" className={styles.bns3_admin_actions_item} onClick={() => editLot(id)}>
              Edit
            </Menu.Item>
            <Menu.Item key="2" className={styles.bns3_admin_actions_item} onClick={() => changeActiveState(id, active)}>
              {active ? 'Deactivate' : 'Activate'}
            </Menu.Item>
            <Menu.Item key="3" className={styles.bns3_admin_actions_item} onClick={handleDeleteButtonClick}>
              Delete
            </Menu.Item>
          </Menu>
        </div>
      );

      return (
        <>
          <div className={styles.bns3_actions_btns}>
            <Dropdown
              menu={menu}
              onOpenChange={open => {
                setIsActionsOpen(open);
              }}
            >
              <span>
                Admin Actions
                {isActionsOpen ? (
                  <UpOutlined className={styles.bns3_actions_btns_icon} />
                ) : (
                  <DownOutlined className={styles.bns3_actions_btns_icon} />
                )}
              </span>
            </Dropdown>
          </div>
          {getFavourite()}
          {getSendMsgActionBtn()}
        </>
      );
    }

    if (isAdmin || userId === userProfile.id) {
      return (
        <>
          <div className={styles.bns3_lot_page_actions_block}>
            {getDeleteActionBtn()}
            {getActivationTriggerBtn()}
          </div>
          {getEditActionBtn()}
        </>
      );
    }

    return (
      <>
        <div className={styles.bns3_lot_page_actions_block}>
          {getDeleteActionBtn()}
          {getFavourite()}
        </div>
        {getEditActionBtn()}
        {getSendMsgActionBtn()}
      </>
    );
  };

  return (
    <div className={styles.bns3_lot_page_control_panel_container}>
      <BackHeader title={title} onBack={handleGoBack} />

      <div className={styles.bns3_lot_page_controls_container}>{getControlBtns()}</div>
    </div>
  );
};

ControlPanel.propTypes = {
  singleLotStore: PropTypes.shape({
    singleLot: PropTypes.shape({
      title: PropTypes.string,
    }),
  }),
};

export default observer(ControlPanel);
